import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/auth/auth';
import { takeReturnPath } from '@/auth/sessionLoss';
import { SignIn } from '@/pages/SignIn';
import { Shell } from '@/components/Shell';
import { BrandMark } from '@/components/BrandMark';
import { Portfolio } from '@/pages/Portfolio';
import { Bucket } from '@/pages/Bucket';
import { GraphPage } from '@/pages/Graph';
import { lazy, Suspense, useEffect } from 'react';
import { DEMO } from '@/api/config';
import { EmptyState } from '@/components/ui';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { CAPABILITY } from '@/lib/vocabulary';

/**
 * Workspace and Package render reconstructed-tool fixtures today. In live
 * builds their chunks (and every fixture behind them) are excluded; the routes
 * render the truthful gate state until the backend capability is IMPLEMENTED.
 */
function GatedSurface({ capability }: { capability: string }) {
  // This read no capability at runtime and still stated one as fact, in the
  // contract's own words: two raw enum names, two schema words, and a machine
  // identifier interpolated into a sentence for a person. In the demo branch
  // there is no backend at all to have advertised anything.
  const meta = CAPABILITY[capability];
  return (
    <EmptyState
      title={meta ? `${meta.name} is not available here` : 'Not available here'}
      description={
        meta
          ? `This release does not offer it. What it would let you do: ${meta.does}.`
          : 'This release does not offer this surface.'
      }
    />
  );
}
/**
 * Per-tool analysis.
 *
 * The demo surface is fixture-backed, so it can never stand in for the live
 * one: wiring it behind a runtime gate would ship invented records the moment
 * the capability opened. There is no live per-tool surface yet, which is the
 * unbuilt contract view rather than a gating mistake.
 *
 * The gate is still read at runtime, so the message is true about the backend
 * rather than frozen at build time, and it says plainly which of the two
 * things is missing.
 */
const WorkspacePage = DEMO
  ? lazy(() => import('@/pages/Workspace').then((m) => ({ default: m.WorkspacePage })))
  : () => (
      <GatedByCapability capability="reconstruction" title="Per-tool analysis">
        <EmptyState
          title="Per-tool analysis is not built here yet"
          description="The backend now serves reconstruction, and this surface has not been written against it. Nothing is shown rather than a stand-in, because the only version that exists runs on invented records."
        />
      </GatedByCapability>
    );
const PackagePage = DEMO
  ? lazy(() => import('@/pages/Package').then((m) => ({ default: m.PackagePage })))
  : () => <GatedSurface capability="packaging" />;
import { GatedByCapability } from '@/pages/GatedByCapability';
import { Operations } from '@/pages/Operations';
import { ChooseBucket } from '@/pages/Choose';
import { SearchLive } from '@/pages/Search';
import { DiscoveryGraphLive } from '@/pages/DiscoveryGraph';
import { BucketWalkthroughLive } from '@/pages/BucketWalkthrough';
import { S3Browser } from '@/pages/S3Browser';
import { PackagingLive } from '@/pages/PackagingLive';
import { BucketFlowPage } from '@/pages/Flow';

/**
 * Shown while the session is still being probed. A signed-in operator must
 * never see the sign-in screen flash on the way to a page they are already
 * authorized for: "not yet known" is not "signed out".
 */
function SessionProbe() {
  return (
    <div className="grid h-screen place-items-center bg-bg">
      <div className="flex animate-pop flex-col items-center gap-3">
        <BrandMark />
        <span className="text-caption text-ink-4">restoring your session…</span>
      </div>
    </div>
  );
}

/**
 * Puts an operator back where they were after signing in again.
 *
 * The backend always returns to the root, deliberately: honouring a returnTo
 * from the query string would be an open redirect. So the destination is held
 * in this tab and applied once, here, after admission - and re-validated as a
 * same-origin path on the way out.
 */
function ReturnToWhereYouWere() {
  const navigate = useNavigate();
  useEffect(() => {
    const path = takeReturnPath();
    if (path && path !== `${window.location.pathname}${window.location.search}`) {
      navigate(path, { replace: true });
    }
  }, [navigate]);
  return null;
}

function Gate() {
  const { status } = useAuth();
  if (status === 'loading') return <SessionProbe />;
  if (status !== 'authenticated') return <SignIn />;
  return (
    <>
    <ReturnToWhereYouWere />
    {/* The outer catch. The one inside the shell keeps the rail alive when a
        PAGE fails; this one exists for a failure in the shell itself, which
        would otherwise take the whole application down to a blank document. */}
    <ErrorBoundary surface="Poseidon">
    <Routes>
      <Route element={<Shell />}>
        <Route index element={DEMO ? <Portfolio /> : <ChooseBucket />} />
        <Route path="buckets/:bucketId" element={DEMO ? <Bucket /> : <BucketFlowPage />} />
        <Route path="buckets/:bucketId/graph" element={DEMO ? <GraphPage /> : <DiscoveryGraphLive />} />
        <Route path="buckets/:bucketId/workspace" element={<Suspense fallback={null}><WorkspacePage /></Suspense>} />
        <Route
          path="buckets/:bucketId/package"
          element={DEMO ? <Suspense fallback={null}><PackagePage /></Suspense> : <PackagingLive />}
        />
        {/* Both of these read one bucket, and both used to be reachable
            without naming one - so they picked. Search defaulted to whichever
            registered bucket came back first, and the walkthrough drew every
            bucket a company owns. The bucket is in the path now, which is the
            only place it can be for the answer to be about the thing you
            chose. */}
        <Route path="buckets/:bucketId/search" element={DEMO ? <GatedSurface capability="search" /> : <SearchLive />} />
        <Route path="buckets/:bucketId/walkthrough" element={<BucketWalkthroughLive />} />
        <Route path="search" element={<Navigate to="/" replace />} />
        <Route path="s3" element={DEMO ? <GatedSurface capability="sourceCatalog" /> : <S3Browser />} />
        <Route path="operations" element={<Operations />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
    </ErrorBoundary>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Gate />
      </AuthProvider>
    </BrowserRouter>
  );
}
